import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

export const useCatchingStore = defineStore('catching', () => {
  const catchings = ref([])
  const currentCatching = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const pagination = ref({ page: 1, perPage: 10, total: 0, totalPages: 1 })
  const filters = ref({
    search: '',
    status: '',
    fitness_status: '',
    start_date: '',
    end_date: '',
    district_id: '',
    colour_id: '',
    sort_by: 'catching_date',
    sort_dir: 'desc',
  })

  const getCatchings = async (page = 1) => {
    loading.value = true
    error.value = null
    try {
      const params = {
        page,
        per_page: pagination.value.perPage,
        ...filters.value,
      }
      const response = await api.get('/operations/catchings', { params })
      catchings.value = response.data?.data || response.data || []
      const pg = response.data?.pagination || {}
      pagination.value = {
        page: pg.current_page || page,
        perPage: pg.per_page || pagination.value.perPage,
        total: pg.total || 0,
        totalPages: pg.total_pages || 1,
      }
      return catchings.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load catchings'
    } finally {
      loading.value = false
    }
  }

  const getCatching = async (id) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.get(`/operations/catchings/${id}`)
      currentCatching.value = response.data?.data || response.data
      return currentCatching.value
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load catching'
    } finally {
      loading.value = false
    }
  }

  const createCatching = async (data) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/operations/catchings', data)
      const created = response.data?.data || response.data
      catchings.value.unshift(created)
      return created
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create catching'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateCatching = async (id, data) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.put(`/operations/catchings/${id}`, data)
      const index = catchings.value.findIndex(c => Number(c.id) === Number(id))
      const updated = response.data?.data || response.data
      if (index >= 0) catchings.value[index] = updated
      currentCatching.value = updated
      return updated
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update catching'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteCatching = async (id) => {
    loading.value = true
    error.value = null
    try {
      await api.delete(`/operations/catchings/${id}`)
      catchings.value = catchings.value.filter(c => String(c.id) !== String(id))
      return true
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete catching'
      throw err
    } finally {
      loading.value = false
    }
  }

  const uploadImages = async (catchingId, formData) => {
    loading.value = true
    error.value = null
    try {
      const response = await api.post(`/operations/catchings/${catchingId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data?.data || response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to upload images'
      throw err
    } finally {
      loading.value = false
    }
  }

  const getImages = async (catchingId) => {
    try {
      const response = await api.get(`/operations/catchings/${catchingId}/images`)
      return response.data?.data?.images || response.data?.images
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load images'
    }
  }

  const deleteImage = async (catchingId, imageId) => {
    loading.value = true
    try {
      await api.delete(`/operations/catchings/${catchingId}/images/${imageId}`)
      return true
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete image'
    } finally {
      loading.value = false
    }
  }

  const getStatistics = async () => {
    try {
      const response = await api.get('/operations/catchings/statistics/overview')
      return response.data?.data || response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load statistics'
    }
  }

  const getNearby = async (latitude, longitude, radius = 5) => {
    try {
      const response = await api.get('/operations/catchings/nearby', {
        params: { latitude, longitude, radius }
      })
      return response.data?.data || response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load nearby catchings'
    }
  }

  const setFilters = (newFilters) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const bulkUpdateFitness = async (ids, fitnessStatus, unfitReason = '') => {
    loading.value = true
    error.value = null
    try {
      const response = await api.patch('/operations/catchings/bulk-fitness', {
        ids,
        fitness_status: fitnessStatus,
        unfit_reason: unfitReason,
      })
      return response.data?.data || response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update fitness status'
      throw err
    } finally {
      loading.value = false
    }
  }

  const setPerPage = (perPage) => {
    pagination.value = {
      ...pagination.value,
      perPage: Number(perPage) || 10,
      page: 1,
    }
  }

  return {
    catchings,
    currentCatching,
    loading,
    error,
    pagination,
    filters,
    getCatchings,
    getCatching,
    createCatching,
    updateCatching,
    deleteCatching,
    bulkUpdateFitness,
    uploadImages,
    getImages,
    deleteImage,
    getStatistics,
    getNearby,
    setFilters,
    setPerPage,
  }
})
